import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useAuthStore } from '@/stores/authStore';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '@/constants/languages';
import type { LanguageCode } from '@/types/api';
import styles from './Header.module.css';

export function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, uiLanguage, toggleTheme, setUiLanguage } = usePreferencesStore();
  const { username, logout } = useAuthStore();

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUiLanguage(e.target.value as LanguageCode);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const logo = 'logo-cinfo-mono-white.svg';

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.brand}>
          <NavLink to="/" className={styles.logoLink}>
            <img 
              src={logo}
              alt="Cinfo" 
              className={styles.logoImage}
            />
            <span className={styles.logoText}>{t('app.title')}</span>
          </NavLink>
        </div>

        <nav className={styles.nav}>
          <NavLink
            to="/thematic"
            className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
          >
            {t('nav.thematic')}
          </NavLink>
          <NavLink
            to="/economic-sector"
            className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
          >
            {t('nav.economicSector')}
          </NavLink>
          <NavLink
            to="/grammatical-reference"
            className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
          >
            {t('nav.grammaticalReference')}
          </NavLink>
          <NavLink
            to="/history"
            className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
          >
            {t('nav.history')}
          </NavLink>
        </nav>

        <div className={styles.controls}>
          {/* Language Switcher - Dropdown */}
          <select
            value={uiLanguage}
            onChange={handleLanguageChange}
            className={styles.languageSelect}
            aria-label={t('language.select')}
          >
            {SUPPORTED_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {LANGUAGE_LABELS[code]}
              </option>
            ))}
          </select>

          {/* Theme Toggle */}
          <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={t('theme.toggle')}
            title={theme === 'light' ? t('theme.dark') : t('theme.light')}
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>

          {/* User info and Logout */}
          {username && (
            <div className={styles.userSection}>
              <span className={styles.username}>{username}</span>
              <button
                className={styles.logoutButton}
                onClick={handleLogout}
                aria-label={t('auth.logout')}
                title={t('auth.logout')}
              >
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
